/*
 *
 * OperationLinks
 *
 */

import React, { useEffect, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from "react-helmet"
import { Icon } from "components"
import { notification, Modal } from "antd"
import { QRCodeSVG } from "qrcode.react"
import {
	setBreadcrumb,
	setLoader,
	setTitle,
	setOperationLinksViewMode,
	setOperationLinksCollapsedGroups,
} from "store/actions"
import { OperationLinkGroup, OperationLink } from "./types"

import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"
import { translate } from "utils/utils"
import "./styles.scss"

/**
 * Translates the title and subtitle of each section based on the selected app language
 * @param sections - Array of operation link groups with potentially multilingual title/subtitle
 * @returns Array of sections with translated title and subtitle
 */
const translateSettingsSections = (
	sections: OperationLinkGroup[]
): OperationLinkGroup[] => {
	return sections.map((section) => ({
		...section,
		title: translate(section.title),
		subtitle: section.subtitle ? translate(section.subtitle) : undefined,
		links: section.links.map((link) => ({
			...link,
			title: translate(link.title),
			subtitle: link.subtitle ? translate(link.subtitle) : undefined,
		})),
	}))
}

const OperationLinks: React.FC = () => {
	const dispatch = useDispatch()
	const [groups, setGroups] = useState<OperationLinkGroup[]>([])
	const [qrModalVisible, setQrModalVisible] = useState(false)
	const [qrModalData, setQrModalData] = useState<{
		url: string
		title: string
	} | null>(null)

	// Get persisted state from Redux store
	const viewMode = useSelector(
		(state: any) => state.operationLinksViewMode || "card"
	)
	const collapsedGroups = useSelector(
		(state: any) => state.operationLinksCollapsedGroups || {}
	)

	const getData = useCallback(async () => {
		dispatch(setLoader(true))

		try {
			// TODO: Replace with actual API call when backend is ready
			const response = await API.get({
				url: Endpoints.uriSettings("backofficeLinksPage"),
			})

			const translatedSections = translateSettingsSections(
				response.data.results?.settings?.sections || []
			)

			if (response.ok) {
				setGroups(translatedSections)

				// Set initial collapsed state based on defaultOpen property only if not already set
				if (Object.keys(collapsedGroups).length === 0) {
					const initialCollapsedState: Record<string, boolean> = {}
					translatedSections.forEach((section, index) => {
						initialCollapsedState[index] = !section.defaultOpen
					})
					dispatch(setOperationLinksCollapsedGroups(initialCollapsedState))
				}
			}
		} catch (err) {
			console.log("API Error", err)
			notification.error({
				message: Strings.serverErrors.title,
				description: Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})
		}

		dispatch(setLoader(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch])

	useEffect(() => {
		dispatch(setTitle(""))
		dispatch(setBreadcrumb(null))
		getData()
	}, [dispatch, getData])

	const toggleGroup = useCallback(
		(groupIndex: number) => {
			dispatch(
				setOperationLinksCollapsedGroups({
					...collapsedGroups,
					[groupIndex]: !collapsedGroups[groupIndex],
				})
			)
		},
		[dispatch, collapsedGroups]
	)

	const handleLinkClick = useCallback((link: OperationLink) => {
		const clickAction = link.click || "openInNewTab"

		switch (clickAction) {
			case "openInNewTab":
				window.open(link.url, "_blank", "noopener,noreferrer")
				break
			case "copyToClipboard":
				navigator.clipboard
					.writeText(link.url)
					.then(() => {
						notification.success({
							message: Strings.operationLinks.copiedToClipboard,
							description: link.url,
							placement: "bottomRight",
							duration: 3,
						})
					})
					.catch(() => {
						notification.error({
							message: Strings.serverErrors.title,
							description: Strings.operationLinks.copyFailed,
							placement: "bottomRight",
							duration: 3,
						})
					})
				break
			case "openQrCode":
				setQrModalData({
					url: link.url,
					title: link.title,
				})
				setQrModalVisible(true)
				break
			case "doNothing":
				// Do nothing
				break
			default:
				window.open(link.url, "_blank", "noopener,noreferrer")
				break
		}
	}, [])

	const renderIcon = useCallback(
		(icon?: OperationLink["icon"], className?: string) => {
			if (!icon) {
				return <Icon name="external-link" className={className || "LinkIcon"} />
			}

			switch (icon.type) {
				case "icomoon":
					return <Icon name={icon.value} className={className || "LinkIcon"} />
				case "url":
					return (
						<img src={icon.value} alt="Link icon" className="LinkIconImage" />
					)
				case "svg":
					return (
						<div
							className="LinkIconSvg"
							dangerouslySetInnerHTML={{ __html: icon.value }}
						/>
					)
				case "image":
					return (
						<img src={icon.value} alt="Link icon" className="LinkIconImage" />
					)
				default:
					return (
						<Icon name="external-link" className={className || "LinkIcon"} />
					)
			}
		},
		[]
	)

	const renderCardView = useCallback(
		(group: OperationLinkGroup, index: number) => {
			const isCollapsed = collapsedGroups[index]

			return (
				<div className={`GroupContent${isCollapsed ? " collapsed" : ""}`}>
					{group.links
						.filter((link) => link.isActive)
						.map((link, linkIndex) => (
							<div
								key={`LinkItem-${linkIndex}`}
								className="LinkCard"
								onClick={() => handleLinkClick(link)}
							>
								<div className="LinkCardIcon">{renderIcon(link.icon)}</div>
								<p className="LinkCardName">{link.title}</p>
								{link.subtitle && (
									<p className="LinkCardDescription">{link.subtitle}</p>
								)}
								<p className="LinkCardUrl">{link.url}</p>
							</div>
						))}
				</div>
			)
		},
		[collapsedGroups, handleLinkClick, renderIcon]
	)

	const renderListView = useCallback(
		(group: OperationLinkGroup, index: number) => {
			const isCollapsed = collapsedGroups[index]

			return (
				<div className={`GroupContentList${isCollapsed ? " collapsed" : ""}`}>
					{group.links
						.filter((link) => link.isActive)
						.map((link, linkIndex) => (
							<div
								key={`LinkItem-${linkIndex}`}
								className="LinkItemList"
								onClick={() => handleLinkClick(link)}
							>
								<div className="LinkIconWrapper">{renderIcon(link.icon)}</div>
								<div className="LinkInfo">
									<p className="LinkName">{link.title}</p>
									{link.subtitle && (
										<p className="LinkDescription">{link.subtitle}</p>
									)}
								</div>
								<Icon name="right-chevron" className="LinkArrow" />
							</div>
						))}
				</div>
			)
		},
		[collapsedGroups, handleLinkClick, renderIcon]
	)

	const renderGroup = useCallback(
		(group: OperationLinkGroup, index: number) => {
			const isCollapsed = collapsedGroups[index]

			return (
				<div key={index} className="LinkGroup">
					<div className="GroupHeader" onClick={() => toggleGroup(index)}>
						<div className="GroupHeaderLeft">
							{renderIcon(group.icon, "GroupIcon")}
							<div>
								<p className="GroupTitle">{group.title}</p>
								{group.subtitle && (
									<p className="GroupDescription">{group.subtitle}</p>
								)}
							</div>
						</div>
						<Icon
							name="right-chevron"
							className={`CollapseIcon${isCollapsed ? " collapsed" : ""}`}
						/>
					</div>
					{viewMode === "card"
						? renderCardView(group, index)
						: renderListView(group, index)}
				</div>
			)
		},
		[
			collapsedGroups,
			toggleGroup,
			viewMode,
			renderCardView,
			renderListView,
			renderIcon,
		]
	)

	return (
		<>
			<Helmet>
				<title>{Strings.sidebar.operationLinks}</title>
				<meta name="description" content="Operation Links" />
			</Helmet>
			<div className="OperationLinksContainer">
				<div className="OperationLinksHeader">
					<div className="HeaderLeft">
						<Icon name="external-link" className="HeaderIcon" />
						<h1 className="HeaderTitle">{Strings.sidebar.operationLinks}</h1>
					</div>
					<div className="ViewToggle">
						<button
							type="button"
							className={`ViewToggleButton${viewMode === "card" ? " active" : ""}`}
							onClick={() => dispatch(setOperationLinksViewMode("card"))}
						>
							<Icon name="grid" />
							{Strings.operationLinks.cardView}
						</button>
						<button
							type="button"
							className={`ViewToggleButton${viewMode === "list" ? " active" : ""}`}
							onClick={() => dispatch(setOperationLinksViewMode("list"))}
						>
							<Icon name="list" />
							{Strings.operationLinks.listView}
						</button>
					</div>
				</div>

				{groups.length > 0 ? (
					<div className="OperationLinksContent">{groups.map(renderGroup)}</div>
				) : (
					<div className="EmptyState">
						<Icon name="external-link" className="EmptyIcon" />
						<p className="EmptyText">{Strings.operationLinks.noLinks}</p>
					</div>
				)}
			</div>

			<Modal
				title={qrModalData?.title}
				visible={qrModalVisible}
				onCancel={() => setQrModalVisible(false)}
				footer={
					qrModalData
						? [
								<button
									key="copy"
									type="button"
									className="ant-btn ant-btn-default"
									onClick={() => {
										navigator.clipboard
											.writeText(qrModalData.url)
											.then(() => {
												notification.success({
													message: Strings.operationLinks.copiedToClipboard,
													description: qrModalData.url,
													placement: "bottomRight",
													duration: 3,
												})
											})
											.catch(() => {
												notification.error({
													message: Strings.serverErrors.title,
													description: Strings.operationLinks.copyFailed,
													placement: "bottomRight",
													duration: 3,
												})
											})
									}}
								>
									<Icon name="duplicate" style={{ marginRight: "8px" }} />
									Copy
								</button>,
								<button
									key="open"
									type="button"
									className="ant-btn ant-btn-primary"
									onClick={() => {
										window.open(
											qrModalData.url,
											"_blank",
											"noopener,noreferrer"
										)
									}}
								>
									<Icon name="external-link" style={{ marginRight: "8px" }} />
									{Strings.generic.open}
								</button>,
							]
						: null
				}
				centered
				className="QrCodeModal"
			>
				{qrModalData && (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							padding: "20px 0",
						}}
					>
						<QRCodeSVG value={qrModalData.url} size={256} level="H" />
						<p
							style={{
								marginTop: "20px",
								wordBreak: "break-all",
								textAlign: "center",
								color: "#666",
								fontSize: "14px",
							}}
						>
							{qrModalData.url}
						</p>
					</div>
				)}
			</Modal>
		</>
	)
}

export default OperationLinks
